import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Paper,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import { FiUpload, FiEdit2, FiTrash2 } from "react-icons/fi";
import { format } from "date-fns";
import { hu } from 'date-fns/locale';
import { useAuth } from '@clerk/clerk-react';

// Az API alap-címe: éles oldalon üres (az nginx továbbítja az /api kéréseket), fejlesztéskor a REACT_APP_API_URL-ből jön
const API = process.env.REACT_APP_API_URL ?? "";

interface DownloadableFile {
    id: number;
    name: string;
    uploadDate: string;
    typeName: string;
    size: number;
}

// ÚJ: A hiba javításához adjuk hozzá ezt az interface-t
interface UploadModalProps {
    disableScrollLock?: boolean;
}

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
    },
}));

const AdminLetolthetoTartalmak: React.FC = () => {
    const { getToken } = useAuth();
    const [openUploadModal, setOpenUploadModal] = useState<boolean>(false);
    const [editingFile, setEditingFile] = useState<DownloadableFile | null>(null);
    const [uploadedFiles, setUploadedFiles] = useState<DownloadableFile[]>([]);
    const [availableTypes, setAvailableTypes] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [openConfirmationModal, setOpenConfirmationModal] = useState<boolean>(false);
    const [confirmationAction, setConfirmationAction] = useState<() => void>(() => {});
    const [confirmationText, setConfirmationText] = useState<string>("");

    const fetchContent = async () => {
        setLoading(true);
        const jwtToken = await getToken();
        if (!jwtToken) {
            console.error("JWT token is missing.");
            setLoading(false);
            return;
        }

        try {
            const [filesResponse, typesResponse] = await Promise.all([
                fetch(`${API}/api/admin/downloadable/all`, {
                    method: "GET",
                    headers: { "Authorization": `Bearer ${jwtToken}` },
                }),
                fetch(`${API}/api/admin/downloadable/types`, {
                    method: "GET",
                    headers: { "Authorization": `Bearer ${jwtToken}` },
                })
            ]);
            
            if (filesResponse.ok && typesResponse.ok) {
                const filesData = await filesResponse.json();
                const typesData = await typesResponse.json();

                setUploadedFiles(filesData);
                const typeNames = typesData.map((type: { name: string }) => type.name);
                setAvailableTypes(typeNames);
            } else {
                console.error("Failed to fetch data:", filesResponse.status, filesResponse.statusText);
            }
        } catch (error) {
            console.error("Failed to fetch data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContent();
    }, []);

    const handleOpenConfirmation = (action: () => void, text: string) => {
        setConfirmationAction(() => action);
        setConfirmationText(text);
        setOpenConfirmationModal(true);
    };

    const handleConfirmAction = () => {
        confirmationAction();
        handleCloseConfirmation();
    };

    const handleCloseConfirmation = () => {
        setOpenConfirmationModal(false);
    };

    const handleOpenUploadModal = (file: DownloadableFile | null = null) => {
        setEditingFile(file);
        setOpenUploadModal(true);
    };

    const handleCloseUploadModal = () => {
        setOpenUploadModal(false);
        setEditingFile(null);
    };

    const handleDeleteFile = async (fileId: number) => {
        const jwtToken = await getToken();
        if (!jwtToken) {
            alert("Jogosultság hiányzik. Kérjük, jelentkezzen be újra.");
            return;
        }

        try {
            const response = await fetch(`${API}/api/admin/downloadable/${fileId}`, {
                method: "DELETE",
                headers: { "Authorization": `Bearer ${jwtToken}` },
            });

            if (response.ok) {
                fetchContent();
            } else {
                alert("Törlés sikertelen! Hiba: " + response.statusText);
            }
        } catch (error) {
            console.error("Deletion failed:", error);
            alert("Hiba történt a törlés során.");
        }
    };

    const UploadModal: React.FC<UploadModalProps> = ({ disableScrollLock }) => {
        const [localForm, setLocalForm] = useState<{ name: string; type: string; file: File | null }>(
            editingFile ? {
                name: editingFile.name,
                type: editingFile.typeName,
                file: null,
            } : {
                name: "",
                type: "",
                file: null,
            }
        );

        const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            const droppedFile = e.dataTransfer.files[0];
            if (droppedFile) {
                setLocalForm({ ...localForm, file: droppedFile });
            }
        };

        const handleUploadSubmit = async () => {
            const isEditing = !!editingFile;
            const requiredFields = [localForm.name, localForm.type];
        
            if (isEditing) {
                if (!requiredFields.some(Boolean) && !localForm.file) {
                    alert("Kérjük, adja meg a fájl nevét, típusát vagy válasszon ki egy új fájlt a módosításhoz!");
                    return;
                }
            } else {
                if (!requiredFields.every(Boolean) || !localForm.file) {
                    alert("Kérjük, töltse ki az összes mezőt és válasszon ki egy fájlt!");
                    return;
                }
            }
        
            const jwtToken = await getToken();
            if (!jwtToken) {
                alert("Jogosultság hiányzik. Kérjük, jelentkezzen be újra.");
                return;
            }
        
            const formData = new FormData();
            formData.append("name", localForm.name);
            formData.append("type", localForm.type);
            if (localForm.file) {
                formData.append("file", localForm.file);
            }
        
            const url = isEditing
                ? `${API}/api/admin/downloadable/${editingFile.id}`
                : `${API}/api/admin/downloadable/upload`;
            const method = isEditing ? "PUT" : "POST";
        
            try {
                const response = await fetch(url, {
                    method: method,
                    headers: { "Authorization": `Bearer ${jwtToken}` },
                    body: formData,
                });
        
                if (response.ok) {
                    await fetchContent();
                    handleCloseUploadModal();
                } else {
                    alert(`Művelet sikertelen! Hiba: ${response.statusText}`);
                }
            } catch (error) {
                console.error("Művelet sikertelen:", error);
                alert("Hiba történt a művelet során.");
            }
        };

        return (
            <Dialog open={openUploadModal} onClose={handleCloseUploadModal} disableScrollLock={disableScrollLock}>
                <DialogTitle>{editingFile ? "Tartalom szerkesztése" : "Letölthető tartalmak feltöltése"}</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <TextField
                            fullWidth
                            label="Fájlnév"
                            variant="outlined"
                            margin="normal"
                            value={localForm.name}
                            onChange={(e) => setLocalForm({ ...localForm, name: e.target.value })}
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel id="file-type-label">Típus</InputLabel>
                            <Select
                                labelId="file-type-label"
                                id="file-type"
                                value={localForm.type}
                                label="Típus"
                                onChange={(e) => setLocalForm({ ...localForm, type: e.target.value })}
                            >
                                {availableTypes.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box
                            sx={{
                                border: "2px dashed",
                                borderColor: 'text.secondary',
                                p: 3,
                                mt: 2,
                                textAlign: "center",
                                cursor: "pointer",
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleFileDrop}
                        >
                            <FiUpload size={40} />
                            <Typography>
                                Húzza ide a fájlokat, vagy{" "}
                                <label htmlFor="file-upload" style={{ textDecoration: "underline", cursor: "pointer" }}>
                                    válasszon fájlt
                                </label>
                            </Typography>
                            <input
                                id="file-upload"
                                type="file"
                                hidden
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    if (e.target.files && e.target.files[0]) {
                                        setLocalForm({ ...localForm, file: e.target.files[0] });
                                    }
                                }}
                            />
                            {localForm.file && <Typography>{localForm.file.name}</Typography>}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseUploadModal} color="secondary">Mégse</Button>
                    <Button onClick={handleUploadSubmit} variant="contained" color="primary">
                        {editingFile ? "Módosítások mentése" : "Feltöltés"}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const ConfirmationModal = () => (
        <Dialog
            open={openConfirmationModal}
            onClose={handleCloseConfirmation}
            disableScrollLock 
        >
            <DialogTitle>Figyelem!</DialogTitle>
            <DialogContent>
                <DialogContentText>{confirmationText}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseConfirmation} color="secondary">Mégse</Button>
                <Button onClick={handleConfirmAction} variant="contained" color="error">Megerősítés</Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <StyledPaper>
            <Box sx={{ mb: 3, display: "flex", flexDirection: { xs: 'column', sm: 'row' }, justifyContent: "space-between", alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
                <Typography variant="h6">Letölthető tartalmak (CSAK .PNG!!!)</Typography>
                <Button
                    variant="contained"
                    startIcon={<FiUpload />}
                    onClick={() => handleOpenUploadModal()}
                >
                    Letölthető tartalmak feltöltése
                </Button>
            </Box>
            <TableContainer  component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Fájlnév</TableCell>
                            <TableCell>Feltöltés dátuma</TableCell>
                            <TableCell>Típus</TableCell>
                            <TableCell>Méret</TableCell>
                            <TableCell>Műveletek</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : uploadedFiles.length > 0 ? (
                            uploadedFiles.map((file) => (
                                <TableRow key={file.id}>
                                    <TableCell>{file.name}</TableCell>
                                    <TableCell>
                                        {format(new Date(file.uploadDate), "yyyy-MM-dd", { locale: hu })}
                                    </TableCell>
                                    <TableCell>{file.typeName}</TableCell>
                                    <TableCell>{(file.size / 1024 / 1024).toFixed(2)} MB</TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleOpenUploadModal(file)}>
                                            <FiEdit2 />
                                        </IconButton>
                                        <IconButton onClick={() => handleOpenConfirmation(() => handleDeleteFile(file.id), "Biztosan törölni szeretné ezt a fájlt? Ez a művelet nem vonható vissza.")} color="error">
                                            <FiTrash2 />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    Nincsenek feltöltött letölthető tartalmak.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <UploadModal disableScrollLock={true} />
            <ConfirmationModal />
        </StyledPaper>
    );
};

export default AdminLetolthetoTartalmak;